// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useEffect, useState, useRef } from 'react';
import WorkspaceService from '../../../services/workspace/WorkspaceService';
import { useOrganizationId } from '../../../state/hooks/OrganizationHooks';
import { useWorkspaceId } from '../../../state/hooks/WorkspaceHooks';

const BACKGROUND_IMAGE_FILENAME = 'interactiveMapBG.txt';

export const useBackgroundImage = () => {
  const [backgroundImageDataUri, setBackgroundImageDataUri] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const hasFetchedRef = useRef(false);

  const fetchBackgroundImage = useCallback(async () => {
    if (hasFetchedRef.current || !organizationId || !workspaceId) return;

    try {
      setIsLoading(true);
      setError(null);
      hasFetchedRef.current = true;

      // eslint-disable-next-line max-len
      const base64 = await WorkspaceService.downloadWorkspaceFileData(
        organizationId,
        workspaceId,
        BACKGROUND_IMAGE_FILENAME
      );

      if (!base64 || typeof base64 !== 'string' || !base64.trim()) return;

      setBackgroundImageDataUri(`data:image/png;base64,${base64.trim()}`);
    } catch (err) {
      console.error('[useBackgroundImage] Failed to fetch background image:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, workspaceId]);

  useEffect(() => {
    fetchBackgroundImage();
  }, [fetchBackgroundImage]);

  return { backgroundImageDataUri, isLoading, error };
};
